import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";

interface JobViewer {
  user_id: string;
  full_name: string;
  logo_url?: string;
  viewed_at: string;
  view_count: number;
  last_view: string;
}

interface JobViewersTableProps {
  viewers: JobViewer[];
}

function MobileViewerCard({ viewer }: { viewer: JobViewer }) {
  return (
    <div className="border rounded-lg p-3 flex items-center gap-3">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={viewer.logo_url} />
        <AvatarFallback>
          {viewer.full_name?.charAt(0)?.toUpperCase() || "U"}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate">{viewer.full_name || "Usuário Anônimo"}</p>
        <p className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(viewer.last_view), {
            addSuffix: true,
            locale: ptBR,
          })}
        </p>
      </div>
      <Badge variant="secondary" className="shrink-0">{viewer.view_count}x</Badge>
    </div>
  );
}

export function JobViewersTable({ viewers }: JobViewersTableProps) {
  const isMobile = useIsMobile();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Usuários que Visualizaram</CardTitle>
        <CardDescription>Candidatos registrados que viram suas vagas</CardDescription>
      </CardHeader>
      <CardContent>
        {viewers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhuma visualização de usuários logados ainda
          </div>
        ) : isMobile ? (
          <div className="space-y-2">
            {viewers.map((viewer) => (
              <MobileViewerCard key={viewer.user_id} viewer={viewer} />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidato</TableHead>
                  <TableHead className="text-center">Visualizações</TableHead>
                  <TableHead className="text-right">Última Visualização</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {viewers.map((viewer) => (
                  <TableRow key={viewer.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={viewer.logo_url} />
                          <AvatarFallback>
                            {viewer.full_name?.charAt(0)?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{viewer.full_name || "Usuário Anônimo"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{viewer.view_count}x</Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(viewer.last_view), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
